import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WorkIcon from '@mui/icons-material/FolderSpecial';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ChatIcon from '@mui/icons-material/Chat';
import { phlebotomistAPI, billsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotifyContext';
import { useMessagingInbox } from '../../hooks/useMessagingInbox';
import AccountSettingsPanel from '../AccountSettingsPanel';
import { MessagingConversationListItem, MessagingMessageRow } from '../MessagingUI';
import { userDisplayLabel } from '../../utils/userDisplayLabel';
import { formatLocalDate, formatLocalDateTime } from '../../utils/formatLocalDate';
import { filterBySearch } from '../../utils/tableSearch';
import TableToolbar from '../TableToolbar';
import Pagination from '../Pagination';
import Modal from '../Modal';
import PaymentsComingSoonModal from '../PaymentsComingSoonModal';
import GradientSpinner from '../GradientSpinner';
import { isStripePaymentsLive } from '../../config/payments';
import '../../App.css';

const PhlebotomistDashboard = ({ activeTab: propTab }) => {
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError, showInfo } = useNotify();
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [bills, setBills] = useState([]);
  const [payoutLink, setPayoutLink] = useState('');
  const [loading, setLoading] = useState(true);
  const dashboard = { availableJobs, myJobs, wallet };
  const [activeTab, setActiveTab] = useState(propTab || 'overview');
  const [bidForm, setBidForm] = useState({ jobId: '', amount: '', estimatedTime: '', message: '' });
  const [jobForBidModal, setJobForBidModal] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalNote, setWithdrawalNote] = useState('');
  const [paymentsComingSoonOpen, setPaymentsComingSoonOpen] = useState(false);

  const {
    conversations,
    messages: chatMessages,
    selectedConvId: selectedConv,
    setSelectedConvId: setSelectedConv,
    messageText,
    setMessageText,
    sendMessage,
    startConversation,
    loading: messagingLoading
  } = useMessagingInbox({
    enabled: activeTab === 'messaging',
    filterConversation: (c) => c.otherParty?.role === 'lab'
  });

  const labsForMessaging = (() => {
    const m = new Map();
    for (const j of [...availableJobs, ...myJobs]) {
      const lab = j.lab;
      if (lab?._id) m.set(String(lab._id), lab);
    }
    return [...m.values()];
  })();

  // Pagination states
  const [availableJobsPage, setAvailableJobsPage] = useState(1);
  const [myJobsPage, setMyJobsPage] = useState(1);
  const [availJobSearch, setAvailJobSearch] = useState('');
  const [availJobStatusFilter, setAvailJobStatusFilter] = useState('');
  const [myJobSearch, setMyJobSearch] = useState('');
  const [myJobStatusFilter, setMyJobStatusFilter] = useState('');
  const [phlebBillsSearch, setPhlebBillsSearch] = useState('');
  const [walletTxSearch, setWalletTxSearch] = useState('');
  const [withdrawalSearch, setWithdrawalSearch] = useState('');
  const itemsPerPage = 10;

  useEffect(() => setAvailableJobsPage(1), [availJobSearch, availJobStatusFilter]);
  useEffect(() => setMyJobsPage(1), [myJobSearch, myJobStatusFilter]);

  useEffect(() => {
    if (propTab) setActiveTab(propTab);
  }, [propTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsRes, myJobsRes, walletRes] = await Promise.all([
        phlebotomistAPI.getAvailableJobs(),
        phlebotomistAPI.getMyJobs(),
        phlebotomistAPI.getWallet()
      ]);
      if (jobsRes.data?.jobs) setAvailableJobs(jobsRes.data.jobs);
      if (myJobsRes.data?.jobs) setMyJobs(myJobsRes.data.jobs);
      if (walletRes.data?.wallet) setWallet(walletRes.data.wallet);
    } catch (err) {
      console.error('Phlebotomist fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBills = async () => {
    try {
      const res = await billsAPI.getBills();
      if (res.data?.bills) setBills(res.data.bills);
    } catch (err) { console.error('Bills fetch error:', err); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchBills();
  }, []);

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    try {
      await phlebotomistAPI.submitBid(bidForm.jobId, {
        amount: parseFloat(bidForm.amount),
        estimatedTime: bidForm.estimatedTime,
        message: bidForm.message
      });
      showSuccess('Bid submitted successfully!');
      setJobForBidModal(null);
      setBidForm({ jobId: '', amount: '', estimatedTime: '', message: '' });
      fetchData();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to submit bid');
    }
  };

  const handleUpdateJobStatus = async (jobId, status) => {
    try {
      await phlebotomistAPI.updateJobStatus(jobId, { status });
      showSuccess(`Job status updated to ${status}!`);
      fetchData();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleViewJobDetails = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
  };

  const filteredAvailJobs = useMemo(() => {
    let rows = dashboard.availableJobs || [];
    if (availJobStatusFilter) rows = rows.filter((j) => j.status === availJobStatusFilter);
    return filterBySearch(rows, availJobSearch, (j) => [
      j.title,
      j.description,
      j.status,
      j.lab?.labName,
      j.lab?.name,
      j.location?.address,
      j.location?.city
    ]);
  }, [dashboard.availableJobs, availJobSearch, availJobStatusFilter]);

  const filteredMyJobs = useMemo(() => {
    let rows = dashboard.myJobs || [];
    if (myJobStatusFilter) rows = rows.filter((j) => j.status === myJobStatusFilter);
    return filterBySearch(rows, myJobSearch, (j) => [
      j.title,
      j.description,
      j.status,
      j.lab?.labName,
      j.lab?.name
    ]);
  }, [dashboard.myJobs, myJobSearch, myJobStatusFilter]);

  const filteredPhlebBills = useMemo(
    () =>
      filterBySearch(bills || [], phlebBillsSearch, (b) => [
        b.invoiceNumber,
        b.type,
        b.status,
        b.amount,
        b.lab?.labName
      ]),
    [bills, phlebBillsSearch]
  );

  const filteredWalletTx = useMemo(() => {
    const txs = dashboard.wallet?.transactions || [];
    return filterBySearch(txs, walletTxSearch, (tx) => [
      tx.type,
      tx.description,
      tx.status,
      tx.amount
    ]);
  }, [dashboard.wallet?.transactions, walletTxSearch]);

  const filteredWithdrawals = useMemo(
    () =>
      filterBySearch(withdrawals || [], withdrawalSearch, (w) => [
        w.status,
        w.amount,
        w.note
      ]),
    [withdrawals, withdrawalSearch]
  );

  const availableJobsStartIndex = (availableJobsPage - 1) * itemsPerPage;
  const availableJobsEndIndex = availableJobsStartIndex + itemsPerPage;
  const paginatedAvailableJobs = filteredAvailJobs.slice(availableJobsStartIndex, availableJobsEndIndex);
  const totalAvailableJobsPages = Math.max(1, Math.ceil(filteredAvailJobs.length / itemsPerPage));

  const myJobsStartIndex = (myJobsPage - 1) * itemsPerPage;
  const myJobsEndIndex = myJobsStartIndex + itemsPerPage;
  const paginatedMyJobs = filteredMyJobs.slice(myJobsStartIndex, myJobsEndIndex);
  const totalMyJobsPages = Math.max(1, Math.ceil(filteredMyJobs.length / itemsPerPage));

  const getPageTitle = () => {
    switch (activeTab) {
      case 'available-jobs': return 'Available Jobs';
      case 'my-jobs': return 'My Jobs';
      case 'wallet': return 'Wallet';
      case 'bills': return 'My Bills';
      case 'analytics': return 'Analytics';
      case 'messaging': return 'Messages';
      case 'settings': return 'Account';
      default: return 'Overview';
    }
  };

  const handleRequestWithdrawal = (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawalAmount);
    if (isNaN(amount) || amount <= 0 || amount > (dashboard.wallet?.balance || 0)) {
      showInfo('Enter a valid amount not exceeding your balance.');
      return;
    }
    setWithdrawals(prev => [{ _id: `wr-${Date.now()}`, amount, status: 'pending', requestedAt: new Date(), note: withdrawalNote }, ...prev]);
    setWithdrawalAmount('');
    setWithdrawalNote('');
    showSuccess('Withdrawal request submitted!');
  };

  return (
    <div>
      {activeTab !== 'overview' && (
        <h1 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          color: 'var(--text-primary)', 
          margin: '0 0 8px 0',
          paddingTop: '0'
        }}>
          {getPageTitle()}
        </h1>
      )}
      {activeTab === 'overview' && dashboard && (
        <>
        <div className="quick-actions">
          <Link to="/dashboard?tab=available-jobs" className="quick-action-btn">
            <span className="quick-action-icon"><AssignmentIcon sx={{ fontSize: 28 }} /></span>
            <span>Available Jobs</span>
          </Link>
          <Link to="/dashboard?tab=my-jobs" className="quick-action-btn">
            <span className="quick-action-icon"><WorkIcon sx={{ fontSize: 28 }} /></span>
            <span>My Jobs</span>
          </Link>
          <Link to="/dashboard?tab=wallet" className="quick-action-btn">
            <span className="quick-action-icon"><AccountBalanceWalletIcon sx={{ fontSize: 28 }} /></span>
            <span>Wallet</span>
          </Link>
          <Link to="/dashboard?tab=messaging" className="quick-action-btn">
            <span className="quick-action-icon"><ChatIcon sx={{ fontSize: 28 }} /></span>
            <span>Messages</span>
          </Link>
        </div>
        <div className="card" style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: 'var(--logo-cyan-teal)' }}>Plan</h3>
          <p style={{ margin: 0, fontWeight: '600' }}>Free plan</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Bids this month: 3 of 5 · Upgrade for unlimited bidding</p>
        </div>
        <div className="grid grid-3">
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Available Jobs</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
              {dashboard.availableJobs?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Open positions</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
            border: '1px solid rgba(181, 255, 51, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>My Jobs</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>
              {dashboard.myJobs?.length || 0}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Assigned jobs</p>
          </div>
          {dashboard.wallet && (
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
              border: '1px solid rgba(51, 255, 217, 0.2)'
            }}>
              <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Wallet Balance</h3>
              <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
                ${dashboard.wallet.balance?.toFixed(2) || '0.00'}
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Available balance</p>
            </div>
          )}
        </div>
        </>
      )}

      {activeTab === 'available-jobs' && (
        <div>
          <Modal
            isOpen={!!jobForBidModal}
            onClose={() => { setJobForBidModal(null); setBidForm({ ...bidForm, jobId: '', amount: '', estimatedTime: '', message: '' }); }}
            title="Submit Bid"
          >
            {jobForBidModal && (
              <>
                <div style={{
                  padding: '16px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.125rem' }}>{jobForBidModal.title}</h3>
                  <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.875rem' }}>{jobForBidModal.description}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                      <p style={{ margin: 0, fontWeight: '500', fontSize: '0.875rem' }}>{jobForBidModal.lab?.labName}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Payment</p>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--logo-cyan-teal)', fontSize: '0.875rem' }}>${jobForBidModal.paymentAmount}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Location</p>
                      <p style={{ margin: 0, fontWeight: '500', fontSize: '0.875rem' }}>{jobForBidModal.location?.address}</p>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleSubmitBid}>
                  <div className="form-group">
                    <label className="form-label">Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={bidForm.amount}
                      onChange={(e) => setBidForm({ ...bidForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estimated Time</label>
                    <input
                      type="text"
                      className="form-input"
                      value={bidForm.estimatedTime}
                      onChange={(e) => setBidForm({ ...bidForm, estimatedTime: e.target.value })}
                      placeholder="e.g., 2 hours"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-textarea"
                      value={bidForm.message}
                      onChange={(e) => setBidForm({ ...bidForm, message: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button type="submit" className="btn btn-primary">Submit Bid</button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => { setJobForBidModal(null); setBidForm({ ...bidForm, jobId: '', amount: '', estimatedTime: '', message: '' }); }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </Modal>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Available Jobs</h2>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {dashboard.availableJobs?.length || 0}
              </div>
            </div>
            <TableToolbar
              search={availJobSearch}
              onSearchChange={setAvailJobSearch}
              placeholder="Search available jobs…"
              resultCount={filteredAvailJobs.length}
              totalCount={dashboard.availableJobs?.length || 0}
            >
              <select
                className="form-select"
                value={availJobStatusFilter}
                onChange={(e) => setAvailJobStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                <option value="open">open</option>
                <option value="bidding">bidding</option>
                <option value="pending_acceptance">pending_acceptance</option>
                <option value="assigned">assigned</option>
                <option value="in-progress">in-progress</option>
                <option value="completed">completed</option>
              </select>
            </TableToolbar>
            <div style={{ display: 'grid', gap: '16px' }}>
              {paginatedAvailableJobs.map((job) => (
                <div key={job._id} className="card" style={{ 
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  padding: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.25rem' }}>{job.title}</h3>
                      <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{job.description}</p>
                    </div>
                    <span className="badge badge-info">{job.status}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                      <p style={{ margin: 0, fontWeight: '500' }}>{job.lab?.labName}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Payment</p>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>${job.paymentAmount}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Location</p>
                      <p style={{ margin: 0, fontWeight: '500' }}>{job.location?.address}</p>
                    </div>
                  </div>
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => handleViewJobDetails(job)}
                      style={{ padding: '8px 16px', minHeight: '44px' }}
                    >
                      View
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setBidForm({ ...bidForm, jobId: job._id });
                        setJobForBidModal(job);
                        setBidForm(prev => ({ ...prev, jobId: job._id }));
                      }}
                    >
                      Submit Bid
                    </button>
                    <Link to="/dashboard?tab=messaging" className="btn btn-outline" style={{ padding: '8px 16px', minHeight: '44px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                      Message
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={availableJobsPage}
              totalPages={totalAvailableJobsPages}
              onPageChange={setAvailableJobsPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredAvailJobs.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'my-jobs' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>My Jobs</h2>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {dashboard.myJobs?.length || 0}
              </div>
          </div>
          <TableToolbar
            search={myJobSearch}
            onSearchChange={setMyJobSearch}
            placeholder="Search my jobs…"
            resultCount={filteredMyJobs.length}
            totalCount={dashboard.myJobs?.length || 0}
          >
            <select
              className="form-select"
              value={myJobStatusFilter}
              onChange={(e) => setMyJobStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="open">open</option>
              <option value="bidding">bidding</option>
              <option value="pending_acceptance">pending_acceptance</option>
              <option value="assigned">assigned</option>
              <option value="in-progress">in-progress</option>
              <option value="completed">completed</option>
            </select>
          </TableToolbar>
          <div style={{ display: 'grid', gap: '16px' }}>
            {paginatedMyJobs.map((job) => (
              <div key={job._id} className="card" style={{ 
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                padding: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.25rem' }}>{job.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{job.description}</p>
                  </div>
                  <span className={`badge ${
                    job.status === 'completed' ? 'badge-success' :
                    job.status === 'pending_acceptance' ? 'badge-warning' :
                    job.status === 'in-progress' ? 'badge-info' :
                    job.status === 'assigned' ? 'badge-warning' : 'badge-info'
                  }`}>
                    {job.status}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '16px' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Payment</p>
                    <p style={{ margin: 0, fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>${job.paymentAmount}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Scheduled</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(job.scheduledDate)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Location</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{job.location?.address}</p>
                  </div>
                </div>
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Link to="/dashboard?tab=messaging" className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                    Message
                  </Link>
                  <button
                    className="btn btn-outline"
                    onClick={() => handleViewJobDetails(job)}
                    style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                  >
                    View Details
                  </button>
                  {job.status === 'pending_acceptance' && (
                    <>
                      <button className="btn btn-primary" onClick={async () => {
                        try {
                          await phlebotomistAPI.acceptJob(job._id);
                          showSuccess('Job accepted');
                          fetchData();
                        } catch (err) {
                          showError(err.response?.data?.message || 'Failed');
                        }
                      }} style={{ padding: '6px 16px', fontSize: '0.875rem' }}>Accept Job</button>
                      <button className="btn btn-outline" onClick={async () => {
                        try {
                          await phlebotomistAPI.declineJob(job._id, {});
                          showInfo('Job declined');
                          fetchData();
                        } catch (err) {
                          showError(err.response?.data?.message || 'Failed');
                        }
                      }} style={{ padding: '6px 16px', fontSize: '0.875rem' }}>Decline Job</button>
                    </>
                  )}
                  {job.assignedPhlebotomist && job.status === 'assigned' && (
                    <>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleUpdateJobStatus(job._id, 'in-progress')}
                        style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                      >
                        Start Job
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleUpdateJobStatus(job._id, 'completed')}
                        style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                      >
                        Complete Job
                      </button>
                    </>
                  )}
                  {job.status === 'in-progress' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleUpdateJobStatus(job._id, 'completed')}
                      style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                    >
                      Complete Job
                    </button>
                  )}
                  {job.status === 'completed' && (
                    (bills || []).some(b => (b.job?._id || b.job)?.toString() === job._id?.toString()) ? (
                      <span className="badge badge-success">Bill sent</span>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={async () => {
                          try {
                            await billsAPI.createBillForJob(job._id, {});
                            showSuccess('Bill sent to lab successfully');
                            fetchBills();
                          } catch (err) {
                            showError(err.response?.data?.message || 'Failed to create bill');
                          }
                        }}
                        style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                      >
                        Send Bill to Lab
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination
            currentPage={myJobsPage}
            totalPages={totalMyJobsPages}
            onPageChange={setMyJobsPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredMyJobs.length}
          />
        </div>
      )}

      {activeTab === 'bills' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>My Bills (Sent to Labs)</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Bills you sent to labs for completed jobs. Labs pay directly (link, cash, or check).</p>
          <TableToolbar
            search={phlebBillsSearch}
            onSearchChange={setPhlebBillsSearch}
            placeholder="Search bills…"
            resultCount={filteredPhlebBills.length}
            totalCount={bills?.length || 0}
          />
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr><th>Invoice</th><th>Lab</th><th>Amount</th><th>Job</th><th>Status</th></tr>
              </thead>
              <tbody>
                {filteredPhlebBills.map((b) => (
                  <tr key={b._id}>
                    <td style={{ fontWeight: '500' }}>{b.invoiceNumber}</td>
                    <td>{b.lab?.labName || b.lab?.name}</td>
                    <td style={{ fontWeight: '600' }}>${b.amount?.toLocaleString()}</td>
                    <td>{b.job?.title || '—'}</td>
                    <td><span className={`badge ${b.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!filteredPhlebBills || filteredPhlebBills.length === 0) && <p style={{ color: 'var(--text-secondary)', padding: '24px', textAlign: 'center' }}>No bills match. Complete jobs and click &quot;Send Bill to Lab&quot; to invoice the lab.</p>}
        </div>
      )}

      {activeTab === 'wallet' && dashboard?.wallet && (
        <div>
          <div className="card" style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
            <h3 className="card-title" style={{ marginBottom: '8px' }}>Payout Link</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>Add your payout link (Stripe Connect, bank transfer, etc.) so labs can pay you and export for accounting.</p>
            <div style={{ marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '8px 16px', fontSize: '0.875rem' }}
                onClick={() => {
                  if (isStripePaymentsLive()) {
                    showInfo('Stripe Connect onboarding will open here once the integration is wired to your Stripe keys.');
                  } else {
                    setPaymentsComingSoonOpen(true);
                  }
                }}
              >
                {isStripePaymentsLive() ? 'Stripe Connect' : 'Connect Stripe (payouts)'}
              </button>
              {!isStripePaymentsLive() && (
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>Automated payouts — preview</span>
              )}
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await phlebotomistAPI.updatePayoutLink({ payoutLink });
                showSuccess('Payout link updated');
              } catch (err) {
                showError(err.response?.data?.message || 'Failed');
              }
            }} className="dashboard-toolbar-row" style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}
            >
              <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
                <label className="form-label">Payout URL</label>
                <input type="url" className="form-input" value={payoutLink} onChange={(e) => setPayoutLink(e.target.value)} placeholder="https://..." />
              </div>
              <button type="submit" className="btn btn-primary">Save</button>
            </form>
          </div>
          {dashboard.wallet.lab && (
            <div className="card" style={{ marginBottom: '24px', background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.08), rgba(51, 255, 217, 0.03))', border: '1px solid rgba(51, 255, 217, 0.25)' }}>
              <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--logo-cyan-teal)' }}>Wallet held with lab</strong>
                {' — '}
                Your balance and payouts are managed with <strong>{dashboard.wallet.lab.labName}</strong>. Withdrawals are processed by the lab.
              </p>
            </div>
          )}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title">Request Withdrawal</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>Submit a withdrawal request. Admin will process manually. Status: Pending → Approved → Paid.</p>
            <form onSubmit={handleRequestWithdrawal} className="dashboard-toolbar-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}
            >
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Amount ($)</label>
                <input type="number" step="0.01" className="form-input" value={withdrawalAmount} onChange={(e) => setWithdrawalAmount(e.target.value)} placeholder="0.00" required style={{ width: '140px' }} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Note (optional)</label>
                <input type="text" className="form-input" value={withdrawalNote} onChange={(e) => setWithdrawalNote(e.target.value)} placeholder="e.g. Bank transfer" style={{ width: '200px' }} />
              </div>
              <button type="submit" className="btn btn-primary">Request Withdrawal</button>
            </form>
          </div>
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title" style={{ marginBottom: '12px' }}>Withdrawal Requests</h2>
            <TableToolbar
              search={withdrawalSearch}
              onSearchChange={setWithdrawalSearch}
              placeholder="Search withdrawals…"
              resultCount={filteredWithdrawals.length}
              totalCount={withdrawals?.length || 0}
            />
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr><th>Amount</th><th>Status</th><th>Requested</th><th>Processed</th></tr>
                </thead>
                <tbody>
                  {filteredWithdrawals.map((w) => (
                    <tr key={w._id}>
                      <td style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>${w.amount?.toFixed(2)}</td>
                      <td><span className={`badge ${w.status === 'paid' ? 'badge-success' : w.status === 'approved' ? 'badge-info' : 'badge-warning'}`}>{w.status}</span></td>
                      <td>{w.requestedAt ? formatLocalDate(w.requestedAt) : '-'}</td>
                      <td>{w.processedAt ? formatLocalDate(w.processedAt) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="grid grid-3" style={{ marginBottom: '24px' }}>
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
              border: '1px solid rgba(51, 255, 217, 0.2)'
            }}>
              <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Balance</h3>
              <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
                ${dashboard.wallet.balance?.toFixed(2)}
              </p>
            </div>
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
              border: '1px solid rgba(181, 255, 51, 0.2)'
            }}>
              <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>Total Earnings</h3>
              <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>
                ${dashboard.wallet.totalEarnings?.toFixed(2)}
              </p>
            </div>
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
              border: '1px solid rgba(51, 255, 217, 0.2)'
            }}>
              <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Total Paid</h3>
              <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>
                ${dashboard.wallet.totalPaid?.toFixed(2)}
              </p>
            </div>
          </div>
          <div className="card">
            <h2 className="card-title">Transactions</h2>
            <TableToolbar
              search={walletTxSearch}
              onSearchChange={setWalletTxSearch}
              placeholder="Search transactions…"
              resultCount={filteredWalletTx.length}
              totalCount={dashboard.wallet?.transactions?.length || 0}
            />
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWalletTx.map((tx) => (
                    <tr key={tx._id}>
                      <td style={{ textTransform: 'capitalize', fontWeight: '500' }}>{tx.type}</td>
                      <td>{tx.description}</td>
                      <td style={{ 
                        color: tx.amount > 0 ? 'var(--logo-cyan-teal)' : '#FF5252',
                        fontWeight: '600'
                      }}>
                        {tx.amount > 0 ? '+' : ''}${Math.abs(tx.amount).toFixed(2)}
                      </td>
                      <td>
                        <span className={`badge ${
                          tx.status === 'paid' ? 'badge-success' :
                          tx.status === 'approved' ? 'badge-info' : 'badge-warning'
                        }`}>
                          {tx.status}
                        </span>
                      </td>
                      <td>{formatLocalDate(tx.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-mobile-card">
              {filteredWalletTx.map((tx) => (
                <div key={tx._id} className="table-mobile-card-item">
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Type</span>
                    <span className="table-mobile-card-value" style={{ textTransform: 'capitalize', fontWeight: '500' }}>{tx.type}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Description</span>
                    <span className="table-mobile-card-value">{tx.description}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Amount</span>
                    <span className="table-mobile-card-value" style={{ 
                      color: tx.amount > 0 ? 'var(--logo-cyan-teal)' : '#FF5252',
                      fontWeight: '600'
                    }}>
                      {tx.amount > 0 ? '+' : ''}${Math.abs(tx.amount).toFixed(2)}
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Status</span>
                    <span className="table-mobile-card-value">
                      <span className={`badge ${
                        tx.status === 'paid' ? 'badge-success' :
                        tx.status === 'approved' ? 'badge-info' : 'badge-warning'
                      }`}>
                        {tx.status}
                      </span>
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Date</span>
                    <span className="table-mobile-card-value">{formatLocalDate(tx.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="grid grid-3" style={{ gap: '24px' }}>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Jobs Completed by Month</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.jobsByMonth || [])?.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.month}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{d.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Earnings by Month</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.earningsByMonth || [])?.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.month}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-lime-green)' }}>${d.amount}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Performance</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Bids submitted</span>
                <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{analytics?.bidsSubmitted ?? 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Completion rate</span>
                <span style={{ fontWeight: '600', color: 'var(--logo-lime-green)' }}>{analytics?.completionRate ?? 0}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <AccountSettingsPanel
          user={user}
          refreshUser={refreshUser}
          onMessage={(m) => {
            if (!m) return;
            const s = String(m).trim();
            if (/^error\s*:/i.test(s)) showError(s.replace(/^error\s*:\s*/i, ''));
            else showSuccess(s);
          }}
        />
      )}

      {activeTab === 'messaging' && (
        <div className="card messaging-card">
          <h2 className="card-title">Messages (with Labs - job related)</h2>
          <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="form-label" style={{ margin: 0 }}>New message</label>
            <select
              className="form-input"
              style={{ maxWidth: '280px' }}
              defaultValue=""
              aria-label="Message a lab from your jobs"
              onChange={(e) => {
                const v = e.target.value;
                if (v) startConversation(v);
                e.target.value = '';
              }}
            >
              <option value="">Select a lab…</option>
              {labsForMessaging.map((lab) => (
                <option key={lab._id} value={lab._id}>{lab.labName || lab.name || lab._id}</option>
              ))}
            </select>
            {messagingLoading && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <GradientSpinner size={22} />
                Loading…
              </span>
            )}
          </div>
          <div className="messaging-layout">
            <div className="messaging-conversation-list">
              {conversations.map((conv) => (
                <MessagingConversationListItem
                  key={conv._id}
                  conv={conv}
                  selectedConvId={selectedConv}
                  onSelect={setSelectedConv}
                  title={userDisplayLabel(conv.otherParty)}
                />
              ))}
            </div>
            <div className="messaging-thread">
              {selectedConv ? (
                <>
                  <div className="messaging-messages">
                    {chatMessages.map((msg) => (
                      <MessagingMessageRow key={msg._id} msg={msg} meUser={user} formatLocalDateTime={formatLocalDateTime} />
                    ))}
                  </div>
                  <form onSubmit={sendMessage} className="messaging-composer">
                    <input type="file" accept="image/*,.pdf" multiple style={{ maxWidth: '120px', padding: '4px', fontSize: '0.75rem' }} title="Attach" disabled />
                    <input type="text" className="form-input" placeholder="Type a message..." value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                    <button type="submit" className="btn btn-primary" disabled={!messageText.trim()}>Send</button>
                  </form>
                </>
              ) : (
                <div className="messaging-empty">Select a conversation</div>
              )}
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={showJobDetails && !!selectedJob}
        onClose={() => { setShowJobDetails(false); setSelectedJob(null); }}
        title="Job details"
        wide
      >
        {selectedJob && (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0', fontSize: '1.125rem' }}>{selectedJob.title}</h3>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{selectedJob.description}</p>
              <p style={{ margin: '12px 0 0 0' }}>
                <span className={`badge ${
                  selectedJob.status === 'completed' ? 'badge-success' :
                  selectedJob.status === 'pending_acceptance' ? 'badge-warning' :
                  selectedJob.status === 'in-progress' ? 'badge-info' : 'badge-info'
                }`}>
                  {selectedJob.status}
                </span>
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{selectedJob.lab?.labName || selectedJob.lab?.name || '—'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Payment</p>
                <p style={{ margin: 0, fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>${selectedJob.paymentAmount ?? '—'}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Scheduled</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{formatLocalDate(selectedJob.scheduledDate)}</p>
              </div>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Location</p>
                <p style={{ margin: 0, fontWeight: '500' }}>{[selectedJob.location?.address, selectedJob.location?.city, selectedJob.location?.state].filter(Boolean).join(', ') || '—'}</p>
              </div>
            </div>
            {selectedJob.bids && selectedJob.bids.length > 0 && (
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>Bids ({selectedJob.bids.length})</p>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {selectedJob.bids.map((bid) => (
                    <div key={bid._id} style={{ padding: '10px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      <p style={{ margin: 0, fontWeight: '500' }}>{bid.phlebotomist?.name || 'Bid'}</p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        ${bid.amount} · {bid.estimatedTime || '—'} · <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>{bid.status}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      <PaymentsComingSoonModal isOpen={paymentsComingSoonOpen} onClose={() => setPaymentsComingSoonOpen(false)} title="Stripe payouts" />
    </div>
  );
};

export default PhlebotomistDashboard;
